/**
 * ProfilePage — shows current plan, coin balance, and payment history.
 *
 * Data fetched in parallel:
 * - GET /billing/subscription → plan details, period_end
 * - GET /billing/payments → payment history
 * - GET /billing/wallet → coin balance
 * - GET /billing/plans → plan metadata (coin packages)
 *
 * Auth: requires login (protected by RequireAuth in App.tsx)
 */

import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useAuthStore } from "@/stores/authStore"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { PlanStatusCard } from "@/components/high/billing/PlanStatusCard"
import { LanguageSelector } from "@/components/medium/LanguageSelector"
import { PurchaseHistoryTable } from "@/components/high/billing/PurchaseHistoryTable"
import {
  getSubscription,
  getPayments,
  getWallet,
  getPlans,
  SubscriptionOut,
  PaymentOut,
  WalletOut,
  PlanOut,
} from "@/services/api"
import { PlanOption } from "@/components/high/billing/PaywallModal"

interface ProfileData {
  subscription: SubscriptionOut | null
  payments: PaymentOut[]
  wallet: WalletOut
  plans: PlanOut[]
}

const PLAN_NAME_MAP: Record<string, string> = {
  free: "Free",
  weekend_pass: "Weekend Pass",
  monthly: "Monthly",
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-48 bg-card border border-border rounded-xl" />
      <div className="h-64 bg-card border border-border rounded-xl" />
    </div>
  )
}

export function ProfilePage() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const [data, setData] = useState<ProfileData | null>(null)
  const [paymentsData, setPaymentsData] = useState<PaymentOut[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [paymentsError, setPaymentsError] = useState(false)

  // Translation is only used for toast copy; keeping it out of the fetch effect
  // deps prevents a refetch whenever the `t` identity changes (e.g. language switch).
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      setLoading(true)

      // Fetch plan data + wallet in parallel; payments separately (partial rendering allowed)
      const [planResult, paymentsResult] = await Promise.allSettled([
        Promise.all([
          getSubscription(),
          getWallet(),
          getPlans(),
        ]),
        getPayments(),
      ])

      if (cancelled) return

      // Handle plan/wallet data
      if (planResult.status === "fulfilled") {
        const [subscription, wallet, plans] = planResult.value
        setData({ subscription, payments: [], wallet, plans })
      } else {
        toast.error(tRef.current("profile.loadPlanError"))
      }

      // Handle payments data (partial rendering — plan shown even if payments fail)
      if (paymentsResult.status === "fulfilled") {
        setPaymentsData(paymentsResult.value)
      } else {
        setPaymentsError(true)
        toast.error(tRef.current("profile.loadHistoryError"))
      }

      setLoading(false)
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [])

  // Determine plan info
  const planTier = user?.plan_tier ?? "free"
  const isPaid = planTier !== "free" && planTier !== null

  // planTier now equals plan.code directly — no mapping needed
  const currentPlanCode = planTier ?? "free"

  const currentPlan = data?.plans.find((p) => p.code === currentPlanCode)
  const planName = currentPlan?.name ?? PLAN_NAME_MAP[currentPlanCode] ?? "Free"

  const balance = data?.wallet.balance ?? 0
  // Coin validity comes from the wallet, which reports no end date once a paid
  // period has lapsed; the subscription keeps the old date and would advertise
  // coins as valid until a moment already in the past.
  const coinsValidUntil = data?.wallet.period_end ?? null

  const periodEnd = data?.subscription?.current_period_end ?? null

  // Access outlives the plan record: a one-off pass keeps its plan after expiry,
  // so the buy button has to come back once the period is over.
  const hasRunningAccess = periodEnd === null || new Date(periodEnd) > new Date()

  // Stripe's customer portal manages subscriptions; a one-off purchase has none.
  const canManagePayments = isPaid && currentPlan?.is_recurring === true

  // Build purchasable plans for PaywallModal (drop free, and the current plan
  // only while its access is still running)
  const upgradePlans: PlanOption[] =
    data?.plans
      .filter(
        (p) => p.is_active && p.code !== "free" && !(p.code === currentPlanCode && hasRunningAccess)
      )
      .map((p) => ({
        id: p.code,
        name: p.name,
        price_pln: p.price_cents / 100,
        coins: p.coins_granted,
        days: p.period_days,
      })) ?? []

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-foreground font-display font-bold text-xl">{t("profile.title")}</h1>
          <Button variant="ghost" asChild className="text-muted-foreground hover:text-foreground">
            <Link to="/">{t("profile.back")}</Link>
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {loading ? (
          <LoadingSkeleton />
        ) : data ? (
          <>
            {/* Language section */}
            <section>
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">{t("profile.language")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <LanguageSelector />
                </CardContent>
              </Card>
            </section>

            {/* My Plan section */}
            <section>
              <PlanStatusCard
                planName={planName}
                planCode={currentPlanCode}
                balance={balance}
                periodEnd={coinsValidUntil}
                isPaid={isPaid}
                hasRunningAccess={hasRunningAccess}
                canManagePayments={canManagePayments}
                upgradePlans={upgradePlans}
              />
            </section>

            {/* Purchase history section */}
            <section>
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">{t("profile.paymentHistory")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {paymentsError ? (
                    <p className="text-destructive text-sm text-center py-6">
                      {t("profile.paymentHistoryError")}
                    </p>
                  ) : (
                    <PurchaseHistoryTable payments={paymentsData ?? []} />
                  )}
                </CardContent>
              </Card>
            </section>
          </>
        ) : (
          <Card className="bg-card border-border p-8 text-center">
            <p className="text-destructive">
              {t("profile.loadDataError")}
            </p>
          </Card>
        )}
      </div>
    </div>
  )
}
